import React, { useMemo, useState } from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import Swal from "sweetalert2";
import { formattaData } from "app/utility/dataOra";
import { annullaErogato, rilasciaRicetta } from "./nreService";
import { useDispatch } from "react-redux";
import LoadingSpinner from "@gull/components/Loading/LoadingSpinner";

/**
 * Props:
 * - show: boolean
 * - title?: string
 * - disableBackdropClose?: boolean
 * - response: JSON "VisualizzaRicetta"
 * - onClose?: (isCancel: boolean) => void
 * - onRicarica?: () => void
 */
export default function VisualizzaRicettaModal({
    show = false,
    title = "Visualizza Ricetta",
    disableBackdropClose = true,
    response,
    onClose,
    onRicarica,
}) {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const ricetta = response.ricetta;
    const info = response.trovatoSuDb ? response.info : null;

    const statoProcessoNum = useMemo(() => Number(ricetta?.statoProcesso ?? 0), [ricetta]);

    const dettagli = useMemo(() => {
        const raw = ricetta?.elencoDettagliPrescrVisualRicetta ?? [];
        if (!Array.isArray(raw)) return [];
        return raw.map((r, idx) => ({
            ...r,
            uniqueKey: `${String(r.codProdPrest ?? "NA")}_${idx}`,
        }));
    }, [ricetta]);

    const nreFormattato = useMemo(() => {
        const nre = ricetta?.nre;
        if (!nre) return "-";
        const s = String(nre);
        return s.length > 5 ? `${s.slice(0, 5)} ${s.slice(5)}` : s;
    }, [ricetta]);

    // --- regole bottoni richieste ---
    const canAnnullaErogato = (statoProcessoNum === 8 || statoProcessoNum === 9) && response.trovatoSuDb;
    const canRilascia = (statoProcessoNum === 5 || statoProcessoNum === 6) && response.trovatoSuDb;

    const getNomeMedico = (input) => {
        if (!input) return "";
        const parts = String(input).split(";");
        const cognome = parts[0]?.split("=")[1]?.trim() || "";
        const nome = parts[1]?.split("=")[1]?.trim() || "";
        return `${cognome} ${nome}`.trim();
    };

    const estraiIndirizzo = (input, part) => {
        if (!input) return "";
        const parts = String(input).split("|");
        switch (part) {
            case 1:
                return parts[0]?.trim() || "";
            case 2: {
                const cap = parts[1]?.trim() || "";
                const comune = parts[2]?.trim() || "";
                if (!cap && !comune) return "";
                return cap && comune ? `${comune} (${cap})` : cap || comune;
            }
            case 3:
                return parts[3]?.trim() || "";
            default:
                return "";
        }
    };

    const toNum = (v) => {
        if (v == null) return 0;
        const s = String(v).trim().replace(",", ".");
        const n = Number(s);
        return Number.isFinite(n) ? n : 0;
    };

    const euro = (n) => {
        try {
            return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);
        } catch {
            return `${(n || 0).toFixed(2)} €`;
        }
    };

    const totali = useMemo(() => {
        const totalePrestazioni = dettagli.reduce((sum, r) => sum + (parseInt(r.quantita, 10) || 0), 0);

        const sommaPrezzi = dettagli.reduce((sum, r) => {
            const q = parseInt(r.quantitaErogata ?? r.quantita ?? "1", 10) || 1;
            return sum + toNum(r.prezzo) * q;
        }, 0);

        const sommaTicketConfezione = dettagli.reduce((sum, r) => sum + toNum(r.ticketConfezione), 0);

        const franchigia = toNum(ricetta?.franchigia);
        const quotaFissa = toNum(ricetta?.quotaFissa);
        const ticket = toNum(ricetta?.ticket);

        return {
            totalePrestazioni,
            sommaPrezzi,
            sommaTicketConfezione,
            franchigia,
            quotaFissa,
            ticket,
        };
    }, [dettagli, ricetta]);

    const statoBadge = (statoRaw) => {
        const stato = Number(statoRaw ?? 0);

        const map = {
            3: { cls: "bg-secondary", label: "DA EROGARE" },
            4: { cls: "bg-danger", label: "ANNULLATA DAL PRESCRITTORE" },
            5: { cls: "bg-info", label: "PRESA IN CARICO" },
            6: { cls: "bg-warning", label: "SOSPESA" },
            7: { cls: "bg-primary", label: "SINGOLA PRESCRIZIONE EROGATA" },
            8: { cls: "bg-success", label: "RICETTA EROGATA" },
            9: { cls: "bg-dark", label: "RI-EROGATA DOPO ANNULLAMENTO" },
        };

        const { cls, label } = map[stato] || { cls: "bg-secondary", label: "STATO NON DISPONIBILE" };

        return (
            <span className="ms-3">
                <Badge className={`${cls} rounded-pill text-white p-2`}>{label}</Badge>
            </span>
        );
    };

    const handleHide = () => {
        if (typeof onClose === "function") onClose(true);
    };

    const handleRicarica = () => {
        if (typeof onRicarica === "function") onRicarica();
    };

    const handleAnnullaErogato = () => {
        Swal.fire({
            title: "Sicuri di voler procedere con l'annullamento dell'erogazione della ricetta?",
            text: "Questa azione non sarà reversibile",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sì, Procedi",
            cancelButtonText: "Annulla",
            reverseButtons: true,
            focusCancel: true,
        }).then((result) => {
            if (!result.isConfirmed) return;
            const richiesta = {
                idRegistro: info.idRegistro,
                idStruttura: info.anagrafica.idstruttura,
                idAnagrafica: info.anagrafica.idanagrafica,
                idimpegnativa: info.idImpegnativa,
                nre: ricetta.nre,
                cfAssistito: info.anagrafica.codiceFiscale,
            };
            setIsLoading(true);
            dispatch(annullaErogato(richiesta))
                .then((resp) => {
                    if (resp.isSuccess) {
                        Swal.fire({
                            title: "Annullamento Ricetta Completato",
                            text: resp.message,
                            icon: "success"
                        }).then(() => {
                            handleHide();
                            handleRicarica();
                        });
                    } else {
                        Swal.fire({
                            title: "Errore",
                            text: resp.message,
                            icon: "error"
                        });
                        console.log(resp);
                    }
                })
                .catch((err) => {
                    const statusError = err?.response ? err.response.data : null;
                    Swal.fire({
                        title: "Errore di sistema",
                        text: statusError || "Contattare assistenza tecnica",
                        icon: "error",
                    });
                    console.error(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        });
    };

    const handleRilascia = async () => {
        const nre = ricetta?.nre ? String(ricetta.nre) : "";
        if (!nre) return;

        const res = await Swal.fire({
            title: "Rilascia ricetta",
            html:
                "Confermi il rilascio della ricetta?<br/><br/>" +
                "L'operazione permetterà ad altri operatori di prendere in carico la ricetta.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sì, Procedi",
            cancelButtonText: "Annulla",
            reverseButtons: true,
            focusCancel: true,
        });

        if (!res.isConfirmed) return;

        handleRilasciaConfirm();
    };

    const handleRilasciaConfirm = () => {
        const richiesta = {
            idRegistro: info.idRegistro,
            idStruttura: info.anagrafica.idstruttura,
            idAnagrafica: info.anagrafica.idanagrafica,
            idimpegnativa: info.idImpegnativa,
            nre: ricetta.nre,
            cfAssistito: info.anagrafica.codiceFiscale,
        };

        setIsLoading(true);
        dispatch(rilasciaRicetta(richiesta))
            .then((resp) => {
                if (resp.isSuccess) {
                    Swal.fire({
                        title: "Rilascio Ricetta Completato",
                        text: resp.message,
                        icon: "success"
                    }).then(() => {
                        handleHide();
                        handleRicarica();
                    });
                } else {
                    Swal.fire({
                        title: "Errore",
                        text: resp.message,
                        icon: "error"
                    });
                    console.log(resp);
                }
            })
            .catch((error) => {
                const statusError = error.response ? error.response.data : null;
                Swal.fire({
                    title: "Errore di sistema",
                    text: statusError ? statusError : "Contattare assistenza tecnica",
                    icon: "error"
                });
                console.log(error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const colonne = [
        {
            dataField: "codProdPrest",
            text: "Codice",
            headerStyle: { width: "12%" },
            style: { width: "12%", whiteSpace: "nowrap" },
            formatter: (_cell, row) => row.codProdPrest ?? "-",
        },
        {
            dataField: "descrProdPrest",
            text: "Prestazione",
            headerStyle: { width: "48%" },
            style: { width: "48%" },
            formatter: (_cell, row) => row.descrProdPrest ?? "-",
        },
        {
            dataField: "quantita",
            text: "Q.tà",
            headerStyle: { width: "6%" },
            style: { width: "6%", textAlign: "center", whiteSpace: "nowrap" },
            formatter: (_cell, row) => row.quantita ?? "-",
        },
        {
            dataField: "prezzo",
            text: "Prezzo",
            headerStyle: { width: "10%" },
            style: { width: "10%", textAlign: "right", whiteSpace: "nowrap" },
            formatter: (_cell, row) => euro(toNum(row.prezzo)),
        },
        {
            dataField: "dataPrenotazione",
            text: "Prenotazione",
            headerStyle: { width: "12%" },
            style: { width: "12%", whiteSpace: "nowrap" },
            formatter: (_cell, row) => (row.dataPrenotazione ? formattaData(row.dataPrenotazione) : "-"),
        },
        {
            dataField: "dataIniErog",
            text: "Erogazione",
            headerStyle: { width: "12%" },
            style: { width: "12%", whiteSpace: "nowrap" },
            formatter: (_cell, row) => (row.dataIniErog ? formattaData(row.dataIniErog) : "-"),
        },
    ];

    return (
        <Modal
            show={show}
            size="xl"
            onHide={handleHide}
            backdrop={disableBackdropClose ? "static" : true}
            centered
            keyboard={!disableBackdropClose}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {title}
                    {statoBadge(ricetta?.statoProcesso)}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ backgroundColor: "#f7f7f7" }}>
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <>
                        {!response.trovatoSuDb && (
                            <div className="alert alert-danger">
                                Questa ricetta non è stata trovata sul database, contattare l'assistenza.
                            </div>
                        )}
                        <>
                            {/* TESTATA */}
                            <div className="container">
                                <div className="row mb-3">
                                    <div className="col-12 border-bottom">
                                        <div className="p-2 d-flex flex-wrap">
                                            <div style={{ width: "50%" }}>
                                                <span className="text-muted me-2">Regione:</span>
                                                <span className="fw-semibold">{ricetta?.codRegione ?? "-"}</span>
                                            </div>
                                            <div style={{ width: "50%" }}>
                                                <span className="text-muted me-2">NRE:</span>
                                                <span className="fw-semibold">{nreFormattato}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="p-2 d-flex flex-wrap">
                                            <div style={{ width: "50%" }}>
                                                <span className="text-muted me-2">Assistito:</span>
                                                <span className="fw-semibold">{ricetta?.cognNome ?? "-"}</span>
                                            </div>
                                            <div style={{ width: "50%" }}>
                                                <span className="text-muted me-2">Codice assistito:</span>
                                                <span className="fw-semibold">{info?.anagrafica?.codiceFiscale ?? "-"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 border-bottom">
                                        <div className="p-2 d-flex flex-wrap">
                                            <div style={{ width: "50%" }}>
                                                <span className="text-muted me-2">Indirizzo:</span>
                                                <span className="fw-semibold">{estraiIndirizzo(ricetta?.indirizzo, 1) || "-"}</span>
                                            </div>
                                            <div style={{ width: "30%" }}>
                                                <span className="text-muted me-2">Città:</span>
                                                <span className="fw-semibold">{estraiIndirizzo(ricetta?.indirizzo, 2) || "-"}</span>
                                            </div>
                                            <div style={{ width: "20%" }}>
                                                <span className="text-muted me-2">Prov:</span>
                                                <span className="fw-semibold">{estraiIndirizzo(ricetta?.indirizzo, 3) || "-"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="p-2 d-flex flex-wrap">
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">ASL assistito:</span>
                                                <span className="fw-semibold">{ricetta?.aslAssistito ?? "-"}</span>
                                            </div>
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Prov assistito:</span>
                                                <span className="fw-semibold">{ricetta?.provAssistito ?? "-"}</span>
                                            </div>
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Esenzione:</span>
                                                <span className="fw-semibold">{ricetta?.codEsenzione ?? "-"}</span>
                                            </div>
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Priorità:</span>
                                                <span className="fw-semibold">{ricetta?.classePriorita ?? "-"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12 border-bottom">
                                        <div className="p-2 d-flex flex-wrap">
                                            <div style={{ width: "50%" }}>
                                                <span className="text-muted me-2">Medico:</span>
                                                <span className="fw-semibold">{getNomeMedico(ricetta?.testata1) || "-"}</span>
                                            </div>
                                            <div style={{ width: "50%" }}>
                                                <span className="text-muted me-2">CF medico:</span>
                                                <span className="fw-semibold">{ricetta?.cfMedico1 ?? "-"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="p-2 d-flex flex-wrap">
                                            <div style={{ width: "65%" }}>
                                                <span className="text-muted me-2">Diagnosi:</span>
                                                <span className="fw-semibold">
                                                    {ricetta?.codDiagnosi ? `${ricetta.codDiagnosi} - ` : ""}
                                                    {ricetta?.descrizioneDiagnosi ?? "-"}
                                                </span>
                                            </div>
                                            <div style={{ width: "35%" }}>
                                                <span className="text-muted me-2">Data compilazione:</span>
                                                <span className="fw-semibold">
                                                    {ricetta?.dataCompilazione ? formattaData(ricetta.dataCompilazione) : "-"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* TABELLA */}
                            <div className="row fw-semibold">
                                <span className="text-center mb-1">PRESTAZIONI</span>
                            </div>
                            <div className="p-3 border-bottom">
                                <BootstrapTable
                                    bootstrap4
                                    keyField="uniqueKey"
                                    bordered={false}
                                    data={dettagli}
                                    columns={colonne}
                                    noDataIndication={"Nessuna prestazione"}
                                    classes="table-bordered table-hover"
                                />
                            </div>

                            {/* TOTALI */}
                            <div className="container">
                                <div className="row mt-3">
                                    <div className="col-12">
                                        <div className="p-2 d-flex flex-wrap">
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Tot prestazioni:</span>
                                                <span className="fw-semibold">{totali.totalePrestazioni}</span>
                                            </div>
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Somma prezzi:</span>
                                                <span className="fw-semibold">{euro(totali.sommaPrezzi)}</span>
                                            </div>
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Ticket (testata):</span>
                                                <span className="fw-semibold">{euro(totali.ticket)}</span>
                                            </div>
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Franchigia:</span>
                                                <span className="fw-semibold">{euro(totali.franchigia)}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="p-2 d-flex flex-wrap">
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Quota fissa:</span>
                                                <span className="fw-semibold">{euro(totali.quotaFissa)}</span>
                                            </div>
                                            <div style={{ width: "25%" }}>
                                                <span className="text-muted me-2">Somma ticket confezione:</span>
                                                <span className="fw-semibold">{euro(totali.sommaTicketConfezione)}</span>
                                            </div>
                                            <div style={{ width: "50%" }}>
                                                <span className="text-muted me-2">Data erogazione:</span>
                                                <span className="fw-semibold">
                                                    {ricetta?.dataErogazione ? formattaData(ricetta.dataErogazione) : "-"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* COMUNICAZIONI */}
                                <details className="mt-3">
                                    <summary className="text-muted">Comunicazioni</summary>
                                    <pre className="mt-2" style={{ fontSize: 12 }}>
                                        {JSON.stringify(ricetta?.elencoComunicazioni, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        </>

                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex justify-content-end flex-grow-1 gap-2">
                    <Button className="w-25" variant="secondary" onClick={handleHide}>
                        Chiudi
                    </Button>
                    {canRilascia && (
                        <Button
                            className="w-25 text-white"
                            variant="danger"
                            onClick={handleRilascia}
                            disabled={isLoading}
                            title="Rilascia ricetta">
                            Rilascia Ricetta
                        </Button>
                    )}
                    {canAnnullaErogato && (
                        <Button
                            variant="danger"
                            className="w-25 text-white"
                            onClick={handleAnnullaErogato}
                            disabled={isLoading}
                            title="Annulla erogato">
                            Annulla Erogato
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
}
